import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { isAxiosError } from 'axios'
import { api, isApiException, QuotationStep, type WorkflowDto } from '@/api/eOrdersApi'
import { useApiAdapter } from '@/store'

export const MAX_BULK_APPROVAL = 50

export interface BulkApprovalResult {
  externalReference: string
  ok: boolean
  status?: number
}

function statusOf(err: unknown): number {
  if (isAxiosError(err)) return err.response?.status ?? 0
  if (isApiException(err)) return err.status
  
  return 0
}

export function useBulkQuotationApproval() {
  const apiAdapter = useApiAdapter()

  const results: Ref<BulkApprovalResult[]> = ref([])
  const processed = ref(0)
  const total = ref(0)
  const isRunning = ref(false)

  const succeeded: ComputedRef<BulkApprovalResult[]> = computed(() => results.value.filter(r => r.ok))
  const failed: ComputedRef<BulkApprovalResult[]> = computed(() => results.value.filter(r => !r.ok))

  function reset() {
    results.value = []
    processed.value = 0
    total.value = 0
    isRunning.value = false
  }
  
  async function run(references: string[]): Promise<void> {
    if (isRunning.value) return
    
    reset()
    isRunning.value = true
    total.value = references.length
    
    try {
      for (const externalReference of references) {
        results.value = [...results.value, await approveOne(externalReference)]
        processed.value += 1
      }
    } finally {
      isRunning.value = false
    }
  }
  
  async function approveOne(externalReference: string): Promise<BulkApprovalResult> {
    let failureStatus: number | undefined

    const workflowResult = await apiAdapter.invokeRaw<WorkflowDto>(
      cfg => api.getWorkflow(externalReference, undefined, undefined, cfg),
      (err: unknown) => { failureStatus = statusOf(err) },
      false
    )

    if (workflowResult.type !== 'success') {
      return { externalReference, ok: false, status: failureStatus ?? 0 }
    }

    const currentStep = workflowResult.data.quotation?.currentProcessStep
    
    if (currentStep !== QuotationStep.ClientApproval) {
      return { externalReference, ok: false, status: 409 }
    }

    const completeResult = await apiAdapter.invokeRaw<WorkflowDto>(
      cfg => api.workflowQuotationCompletestep(externalReference, cfg),
      (err: unknown) => { failureStatus = statusOf(err) },
      false
    )

    return completeResult.type === 'success' ? { externalReference, ok: true } : { externalReference, ok: false, status: failureStatus ?? 0 }
  }
  
  return { results, processed, total, isRunning, succeeded, failed, reset, run }
}